import { message } from "antd";
import { useCallback, useRef } from "react";

import { type EntityIdJobIdResponse } from "types/apiResponse/apiResponse";

import { showApixErrorMessage } from "utilities/api/apix-handler";

import { useSpecificationsSaveToCatalogByIdPostV2 } from "handlers/specifications/specifications";

/**
 * Pure: "Save to catalog" action visibility. Only for builders with add permission on a regular
 * job-level spec when the Global Spec Catalog flag is on — never for general-job (catalog) specs,
 * since a catalog spec cannot be saved back to itself. Shared by the Book View viewer, the AG Grid
 * detail modal, and the grid row-actions menu so all three surfaces gate identically.
 */
export function canShowSaveToCatalogButton({
    isBuilder,
    canAdd,
    actionsVisible,
    isGeneralJob,
    isGlobalSpecCatalogEnabled,
}: {
    isBuilder: boolean;
    canAdd: boolean;
    actionsVisible: boolean;
    isGeneralJob: boolean;
    isGlobalSpecCatalogEnabled: boolean;
}) {
    return actionsVisible && isBuilder && canAdd && !isGeneralJob && isGlobalSpecCatalogEnabled;
}

/**
 * Shared "Save to catalog" mutation for all specification surfaces. Copies a job-level spec up to
 * the company catalog as a new, independent spec, then shows a success/error toast. Guards against
 * concurrent duplicate submissions for the same spec id (e.g. a double-click on a grid row action)
 * so a single spec can't be copied to the catalog twice.
 */
export function useSaveSpecificationToCatalog() {
    const { mutateAsync } = useSpecificationsSaveToCatalogByIdPostV2();
    const inFlightIds = useRef(new Set<number>());

    const saveToCatalog = useCallback(
        async ({ id, jobId }: EntityIdJobIdResponse): Promise<boolean> => {
            // Ignore a repeat submit while the first request for this spec is still in flight.
            if (inFlightIds.current.has(id)) {
                return false;
            }
            inFlightIds.current.add(id);
            try {
                const response = await mutateAsync({ id, params: { jobId } });
                void message.success(`"${response.data.title}" saved to catalog`);
                return true;
            } catch (error) {
                // showApixErrorMessage reads only `.message`; an AxiosError satisfies that shape.
                showApixErrorMessage(error as Error, "Failed to save specification to catalog");
                return false;
            } finally {
                inFlightIds.current.delete(id);
            }
        },
        [mutateAsync],
    );

    return { saveToCatalog };
}
